import { useEffect, useRef } from 'react'
import { useRecoilCallback, useRecoilValue, type RecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import type { TFunction } from 'i18next'
import type { ApplicationType, Content } from '@messenger/types'
import { pageTurnoverRunId } from '@messenger/utils'
import { LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE } from '@messenger/consts'
import { turnOverPageSetToLotus } from '@/api/lotusTurnover'
import { EDITOR_TEXT_BOX_CONVERSION_VERSION_REASON } from '@/api/orderProducts'
import {
  BULK_PROCESSING_TURNOVER_ACTION,
  bulkProcessingProgressAtom,
  canvasPageIdAtom,
  editorLoadFailedKeyAtom,
  editorPageSetTurnedOverPromptAtom,
  editorPagesAtom,
  isBulkProcessingAtom,
  isEditorReadyAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import { caseAtom } from '@/atoms/caseAtoms'
import {
  editorOrderPageSetAtom,
  selectedPageAtom,
  triggerUseEffect,
} from '@/atoms/editorAtoms'
import {
  editorPageContentAtomFamily,
  editorRemintedItemIdsAtom,
  editorTextBoxDraftsAtom,
  editorTextBoxPendingDeletesAtom,
} from '@/atoms/editorTextBoxAtoms'
import { applicationTypeAtom } from '@/atoms/globalAtoms'
import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import type { EditorSaveVersionFallbackCause } from '@/hooks/editor/useEditorSaveVersion'
import { useEditorFlushTextBoxGeometry } from '@/hooks/editor/useEditorFlushTextBoxGeometry'
import { useEditorSaveGesture } from '@/hooks/editor/useEditorSaveGesture'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { useSetToast } from '@/hooks/utility/useSetToast'
import {
  editorCaseServiceIds,
  editorPageSetHeightlessItems,
  editorPageSetPageId,
  editorPageSetTurnover,
  editorPageSetTurnoverToastText,
  editorTextBoxPendingWrites,
  measureEditorTurnoverTextHeights,
  type EditorPageSetTurnoverHeight,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { isOrderPageTurnedOverError } from '@/utils/editor/orderPageTurnedOver'
import { logger, EventCategory } from '@/utils/logger'

// The atom is declared in a JavaScript module, so its type is read from its
// default alone.
const editorLoadKeyAtom = triggerUseEffect as unknown as RecoilState<number>

/** A refusal the editor explains in its own words, and the error the log keeps. */
class TurnoverRefusal extends Error {
  constructor(
    readonly copy: string,
    cause: string,
  ) {
    super(cause)
    this.name = 'TurnoverRefusal'
  }
}

const noVersionCopy = (
  cause: EditorSaveVersionFallbackCause,
  t: TFunction,
): string =>
  cause === 'skipped_for_session'
    ? t('editor.textBox.turnover.noVersionReload')
    : t('editor.textBox.turnover.noVersion')

type RouteError = {
  code?: string
  response?: { status?: number; data?: { data?: unknown; code?: unknown } }
}

/**
 * The words a failed write is shown in. A call that got no answer (a timeout,
 * a dropped connection) may still have committed, so it says the result is
 * unknown; the server's transaction makes either outcome whole.
 */
const routeFailureCopy = (error: unknown, t: TFunction): string => {
  const { code, response } = (error ?? {}) as RouteError
  if (response === undefined || code === 'ECONNABORTED')
    return t('editor.textBox.turnover.unconfirmed')
  if (response.data?.code === LOTUS_FUNERAL_HOME_NOT_ENABLED_CODE)
    return t('editor.textBox.turnover.funeralHomeNotEnabled')
  if (response.status === 409 || response.status === 404)
    return t('editor.textBox.turnover.changed')
  const max = (response.data?.data as { maxBoxesPerPage?: unknown } | undefined)
    ?.maxBoxesPerPage
  if (response.status === 400 && typeof max === 'number')
    return t('editor.textBox.turnover.tooManyBoxes', { max })
  return t('editor.textBox.turnover.failed')
}

const asError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error))

type TurnoverAnswerPages = Awaited<
  ReturnType<typeof turnOverPageSetToLotus>
>['data']['pages']

/**
 * Turns the open page set over to Lotus text boxes, for good: every TinyMCE
 * text box the converter accepts converts and a refused one stays as it
 * was, every page's JSON takes a fresh run id,
 * and the whole set is written in one server transaction together with the
 * edits pending in the editor. It runs as a Save of the line, under the
 * tab's save latch and behind a `text_box_conversion` version, which is the
 * only way back; without that version nothing is written. The editor is
 * blocked while it runs. A box stored with no height takes its painted frame
 * on the page on screen and its text's measured height on any other page.
 *
 * On success every page's rows are set from the answer, the pending work is
 * cleared, the editor reloads its data, and one toast reports the run once
 * the reloaded editor is ready, or once the reload ends without loading.
 * Each page goes with the run id it carried when the editor loaded it, and
 * the server refuses the whole turnover when a stored page carries another,
 * because another tab turned the set over since; that refusal opens the
 * reload prompt a stale Save opens. Any other refusal leaves the editor as it
 * was and shows one error, which names the reason when the order's funeral
 * home is not one the Lotus limit lists; a call that got no answer says its
 * result could not be confirmed. Answers whether the page set was turned over.
 */
export const useEditorTurnOverPageSet = () => {
  const { t, i18n } = useTranslation()
  const runSaveGesture = useEditorSaveGesture()
  const flushGeometry = useEditorFlushTextBoxGeometry()
  const syncCurrentPageToEditorPages = useSyncCurrentPageToEditorPages()
  const handleError = useErrorHandler('useEditorTurnOverPageSet')
  const setToast = useSetToast()

  // The result toast waits for the reloaded editor: raised during the load
  // it would be lost behind it. The overlay waits with it. A reload that
  // fails never reports ready, so its end, under the key this run's reload
  // took, raises the toast instead: the turnover committed either way.
  const pendingToastRef = useRef<string | null>(null)
  const reloadKeyRef = useRef<number | null>(null)
  // Whether the overlay up now is this hook's: another bulk action's overlay
  // is that action's to take down.
  const ownsOverlayRef = useRef(false)
  const isEditorReady = useRecoilValue(isEditorReadyAtom)
  const failedLoadKey = useRecoilValue(editorLoadFailedKeyAtom)
  const releaseOverlay = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        if (!ownsOverlayRef.current) return
        ownsOverlayRef.current = false
        set(isBulkProcessingAtom, false)
        reset(bulkProcessingProgressAtom)
      },
    [],
  )
  const reloadEnded =
    isEditorReady ||
    (failedLoadKey !== null && failedLoadKey === reloadKeyRef.current)
  useEffect(() => {
    if (!reloadEnded || pendingToastRef.current === null) return
    const text = pendingToastRef.current
    pendingToastRef.current = null
    reloadKeyRef.current = null
    releaseOverlay()
    setToast({ text })
    // setToast is a new function on every render; the toast is raised once,
    // when the reload this run started ends.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadEnded, releaseOverlay])

  // Read at the moment of the call, not from the snapshot the run started
  // with: the flush and the sync write after it began.
  const readEditor = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        return {
          canvasPageId:
            snapshot.getLoadable(canvasPageIdAtom).valueMaybe() ?? null,
          selectedPage: snapshot.getLoadable(selectedPageAtom).valueMaybe() as
            | number
            | undefined,
          pages: (snapshot.getLoadable(editorPagesAtom).valueMaybe() ??
            []) as unknown as Content[],
          drafts:
            snapshot.getLoadable(editorTextBoxDraftsAtom).valueMaybe() ?? {},
          pendingDeletes:
            snapshot
              .getLoadable(editorTextBoxPendingDeletesAtom)
              .valueMaybe() ?? {},
          caseServiceIds: editorCaseServiceIds(
            snapshot.getLoadable(caseAtom).valueMaybe(),
          ),
          paintedHeight: (itemId: string): number =>
            snapshot
              .getLoadable(itemControlStateAtomFamily(itemId))
              .valueMaybe()?.position.height ?? 0,
        }
      },
    [],
  )

  const block = useRecoilCallback(
    ({ set, snapshot }) =>
      () => {
        if (snapshot.getLoadable(isBulkProcessingAtom).valueMaybe() === true)
          return
        ownsOverlayRef.current = true
        set(isBulkProcessingAtom, true)
        set(bulkProcessingProgressAtom, {
          processedCount: 0,
          totalCount: 0,
          currentAction: BULK_PROCESSING_TURNOVER_ACTION,
        })
      },
    [],
  )

  /** Every page's rows from the answer, the pending work cleared, and the editor reloading. */
  const applyAndReload = useRecoilCallback(
    ({ set, snapshot }) =>
      (pages: TurnoverAnswerPages) => {
        // Each page's slot takes its answered bundle and is never reset to
        // null: a late all-pages margin check would refill a null slot with
        // the rows it read before the turnover.
        for (const { pageId, bundle } of pages)
          if (bundle !== null) set(editorPageContentAtomFamily(pageId), bundle)
        set(editorTextBoxDraftsAtom, {})
        set(editorTextBoxPendingDeletesAtom, {})
        set(editorRemintedItemIdsAtom, {})
        set(unsavedChangesAtom, false)
        // Down in the same commit as the reload, so the ready the toast
        // waits for is the reloaded editor's, not this one's.
        set(isEditorReadyAtom, false)
        // The key Editor.js's load effect reloads on, as apply layout bumps it.
        const previous =
          snapshot.getLoadable(editorLoadKeyAtom).valueMaybe() ?? 0
        const reloadKey = Math.max(Date.now(), previous + 1)
        reloadKeyRef.current = reloadKey
        set(editorLoadKeyAtom, reloadKey)
      },
    [],
  )

  const promptTurnedOver = useRecoilCallback(
    ({ set }) =>
      () =>
        set(editorPageSetTurnedOverPromptAtom, true),
    [],
  )

  return useRecoilCallback(
    ({ snapshot }) =>
      async (): Promise<boolean> => {
        const refuse = (copy: string, error: Error): false => {
          releaseOverlay()
          handleError(copy, error)
          return false
        }
        const [orderPageSet, applicationType] = await Promise.all([
          snapshot.getPromise(editorOrderPageSetAtom) as Promise<{
            id?: string
          } | null>,
          // The atom is declared in a JavaScript module, so its value
          // arrives untyped.
          snapshot.getPromise(applicationTypeAtom) as Promise<ApplicationType>,
        ])
        const orderPageSetId = orderPageSet?.id
        if (!orderPageSetId)
          return refuse(
            t('editor.textBox.turnover.failed'),
            new Error('turnover without an order page set'),
          )

        block()
        const outcome = await runSaveGesture(
          'turn_over_page_set',
          applicationType,
          async (coveringVersion): Promise<boolean> => {
            if (coveringVersion.versionNumber === undefined)
              return refuse(
                noVersionCopy(coveringVersion.fallbackCause, t),
                new Error(
                  `no version covers the turnover of ${orderPageSetId}: ${coveringVersion.fallbackCause}`,
                ),
              )
            const runId = uuidv4()
            let body: Parameters<typeof turnOverPageSetToLotus>[1]
            let turnover: ReturnType<typeof editorPageSetTurnover>
            try {
              const flushed = await flushGeometry()
              const current = readEditor()
              const selectedPageId = editorPageSetPageId(
                current.pages[(current.selectedPage ?? 1) - 1],
              )
              // The sync writes the canvas into the selected page; while
              // the canvas still holds another page, that would store one
              // page's items over another's.
              if (
                current.canvasPageId === null ||
                current.canvasPageId !== selectedPageId
              )
                throw new TurnoverRefusal(
                  t('editor.textBox.turnover.failed'),
                  `the canvas holds page ${current.canvasPageId} while page ${selectedPageId} is selected`,
                )
              const pages = (await syncCurrentPageToEditorPages()) as Content[]
              const drafts = { ...current.drafts, ...flushed }
              // The run id each page carried as this tab loaded it, before
              // this run's id replaces it in the JSON written.
              const loadedRunIds = new Map(
                pages.map((page) => [
                  editorPageSetPageId(page),
                  pageTurnoverRunId(page) ?? null,
                ]),
              )

              const heights: Record<string, EditorPageSetTurnoverHeight> = {}
              const offScreen: Array<{
                id: string
                html: string
                width: number
              }> = []
              for (const page of pages)
                for (const item of editorPageSetHeightlessItems(page)) {
                  if (editorPageSetPageId(page) === current.canvasPageId) {
                    const painted = current.paintedHeight(item.id)
                    if (painted > 0)
                      heights[item.id] = { height: painted, source: 'painted' }
                  } else if (item.html !== null)
                    offScreen.push({
                      id: item.id,
                      html: item.html,
                      width: item.width,
                    })
                }
              const measured = await measureEditorTurnoverTextHeights(offScreen)
              for (const [itemId, height] of Object.entries(measured))
                heights[itemId] = { height, source: 'measured' }

              turnover = editorPageSetTurnover({
                pages,
                heights,
                caseServiceIds: current.caseServiceIds,
                runId,
              })
              const pending = editorTextBoxPendingWrites({
                drafts,
                pendingDeletes: current.pendingDeletes,
              })
              const setPageIds = new Set(
                turnover.pages.map((page) => page.pageId),
              )
              if (setPageIds.has(null))
                throw new TurnoverRefusal(
                  t('editor.textBox.turnover.failed'),
                  `a page of ${orderPageSetId} holds no order page id`,
                )
              const outside = [...pending.keys()].filter(
                (pageId) => !setPageIds.has(pageId),
              )
              if (outside.length > 0)
                throw new TurnoverRefusal(
                  t('editor.textBox.turnover.failed'),
                  `pending text-box work names pages outside ${orderPageSetId}: ${outside.join(', ')}`,
                )
              body = {
                applicationType,
                coveringVersionNumber: coveringVersion.versionNumber,
                runId,
                pages: turnover.pages.map((page) => {
                  const pageId = page.pageId as string
                  const writes = pending.get(pageId)
                  return {
                    pageId,
                    content: JSON.stringify(page.content),
                    loadedRunId: loadedRunIds.get(pageId) ?? null,
                    boxes: [...page.boxes, ...(writes?.boxes ?? [])],
                    deleteBoxIds: writes?.deleteBoxIds ?? [],
                  }
                }),
              }
            } catch (error) {
              return refuse(
                error instanceof TurnoverRefusal
                  ? error.copy
                  : t('editor.textBox.turnover.failed'),
                asError(error),
              )
            }

            let answer: Awaited<ReturnType<typeof turnOverPageSetToLotus>>
            try {
              answer = await turnOverPageSetToLotus(orderPageSetId, body)
            } catch (error) {
              logger.info({
                message: 'editor_turn_over_page_set_refused',
                category: EventCategory.SAVE,
                data: {
                  orderPageSetId,
                  runId,
                  coveringVersionNumber: coveringVersion.versionNumber,
                  status: (error as RouteError)?.response?.status ?? null,
                  code: (error as RouteError)?.code ?? null,
                },
              })
              // Another tab turned the set over after this one loaded it:
              // the prompt a stale Save raises offers the reload.
              if (isOrderPageTurnedOverError(error)) {
                releaseOverlay()
                promptTurnedOver()
                return false
              }
              return refuse(routeFailureCopy(error, t), asError(error))
            }

            pendingToastRef.current = editorPageSetTurnoverToastText(
              turnover.convertedCount,
              turnover.report,
              t,
              i18n.resolvedLanguage,
            )
            applyAndReload(answer.data.pages)
            logger.info({
              message: 'editor_turn_over_page_set_success',
              category: EventCategory.SAVE,
              data: {
                orderPageSetId,
                runId,
                coveringVersionNumber: coveringVersion.versionNumber,
                pageIds: body.pages.map((page) => page.pageId),
                convertedCount: turnover.convertedCount,
                skipped: turnover.skipped,
                report: turnover.report,
              },
            })
            return true
          },
          EDITOR_TEXT_BOX_CONVERSION_VERSION_REASON,
        )
        // A turnover that never ran, because another Save holds the line,
        // turned nothing over.
        if (outcome === undefined) releaseOverlay()
        return outcome === true
      },
    [
      t,
      i18n,
      runSaveGesture,
      flushGeometry,
      syncCurrentPageToEditorPages,
      handleError,
      readEditor,
      block,
      applyAndReload,
      releaseOverlay,
      promptTurnedOver,
    ],
  )
}
